/** Load the next complete, availability-ordered page before appending its cards. */
if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.button = this.querySelector('.load-more');
      this.container = this.closest('#ProductGridContainer') || document;
      this.grid = this.container.querySelector('#product-grid');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.onClick = event => { event.preventDefault(); this.loadProducts(); this.button?.blur(); };
      this.onSorted = () => this.activate();
      this.onCancel = () => {
        this.paused = true;
        this.request?.abort();
        this.observer?.disconnect();
        if (this.button) this.button.disabled = true;
      };
      this.button?.addEventListener('click', this.onClick);
      document.addEventListener('collection:availability-sorted', this.onSorted);
      document.addEventListener('collection:availability-cancel', this.onCancel);
      this.activate();
    }

    disconnectedCallback() {
      this.connected = false;
      this.request?.abort();
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('collection:availability-sorted', this.onSorted);
      document.removeEventListener('collection:availability-cancel', this.onCancel);
    }

    metadata(root = this.container) {
      const node = root.querySelector('[data-ranking-page]');
      return node ? JSON.parse(node.textContent) : null;
    }

    activate() {
      if (!this.isConnected || !this.grid?.isConnected) return;
      const meta = this.metadata();
      this.ranked = Boolean(meta);
      this.paused = this.ranked && this.grid.dataset.availabilitySorted !== 'true';
      if (this.button) this.button.disabled = this.paused;
      if (this.paused) return;
      const urlPage = Number(new URL(location.href).searchParams.get('page')) || 1;
      this.currentPage = Number(this.grid.dataset.rankingPage) || meta?.page || urlPage;
      this.totalPages = Number(this.grid.dataset.rankingPages) || meta?.pages || (meta ? Math.ceil(meta.count / meta.size) : null);
      this.i = this.currentPage + 1;
      if (this.totalPages !== null && this.currentPage >= this.totalPages) return this.finish();
      this.finished = false;
      if (this.classList.contains('pagination-type--infinite') && !this.observer) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting || entry.intersectionRatio === 1)) this.loadProducts();
        }, { threshold: [0, 1] });
      }
      this.observer?.observe(this);
    }

    finish() {
      this.finished = true;
      this.observer?.disconnect();
      if (this.button) { this.button.disabled = true; this.button.classList.add('visually-hidden'); }
      this.removeAttribute('loading');
    }

    addUrlParam(search, key) {
      const params = new URLSearchParams(search);
      params.set(key, String(this.i));
      return '?' + params.toString();
    }

    stillCurrent(controller, grid, href) {
      return !controller.signal.aborted && this.isConnected && !this.paused &&
        this.grid === grid && grid.isConnected && this.container.querySelector('#product-grid') === grid && location.href === href;
    }

    async loadProducts() {
      if (this.paused || this.finished || this.hasAttribute('loading') || !this.isConnected) return;
      const grid = this.grid;
      if (!grid?.isConnected) return;
      const href = location.href;
      const url = new URL(href);
      const page = this.i;
      url.searchParams.set('page', String(page));
      const controller = new AbortController();
      this.request = controller;
      this.setAttribute('loading', 'true');
      let appended = null;
      try {
        let html;
        if (this.ranked && this.metadata()?.kind === 'search' && window.CollectionAvailability?.searchPage) {
          html = await window.CollectionAvailability.searchPage(url.href, controller.signal);
        } else {
          const response = await fetch(url.href, { signal: controller.signal });
          if (!response.ok) throw new Error('Pagination request failed');
          html = await response.text();
          if (this.ranked) {
            if (!window.CollectionAvailability) throw new Error('Product ordering is not ready');
            html = await window.CollectionAvailability.prepare(html, url.href, controller.signal);
          }
        }
        if (!this.stillCurrent(controller, grid, href)) return;
        appended = this.renderProducts(html, page);
        if (appended === null) return;
        this.container.querySelector('[data-ranking-error]')?.remove();
        if (this.button) this.button.style.removeProperty('display');
        this.currentPage = page;
        this.i = page + 1;
        if (this.ranked) grid.dataset.rankingPage = String(page);
        if (this.totalPages !== null ? page >= this.totalPages : appended === 0) this.finish();
        dispatchCustomEvent('pagination:page-changed', { url: url.pathname + url.search });
      } catch (error) {
        if (this.stillCurrent(controller, grid, href)) {
          // Keep the page number unchanged, so a button click can retry the failed page.
          this.observer?.disconnect();
          // Infinite mode normally hides this button; expose a usable retry on failure.
          if (this.button) { this.button.disabled = false; this.button.style.display = 'inline-flex'; }
          window.CollectionAvailability?.showError();
        }
      } finally {
        if (this.request === controller) this.removeAttribute('loading');
      }
      // Promotions may leave an empty source page; keep loading until a nonempty page or the actual end.
      if (appended === 0 && !this.finished && this.classList.contains('pagination-type--infinite') && this.stillCurrent(controller, grid, href)) this.loadProducts();
      else if (appended !== null && !this.finished && this.stillCurrent(controller, grid, href)) {
        // Re-observing produces a fresh intersection even when the sentinel stayed visible.
        this.observer?.disconnect();
        this.observer?.observe(this);
      }
    }

    renderProducts(html, page) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.getElementById('product-grid');
      if (!incoming) {
        if (this.ranked) throw new Error('Missing ordered product grid');
        this.finish();
        return null;
      }
      if (this.ranked) {
        const meta = this.metadata(parsed);
        const incomingPage = Number(incoming.dataset.rankingPage) || meta?.page;
        if (incoming.dataset.availabilitySorted !== 'true' || incomingPage !== page) throw new Error('Incomplete ordered page');
        const pages = Number(incoming.dataset.rankingPages) || meta?.pages;
        if (pages && this.totalPages !== pages) throw new Error('Search results changed');
      }
      const products = [...incoming.children].filter(node => node.classList.contains('column'));
      if (this.ranked) {
        const existing = [...this.grid.children].filter(node => node.hasAttribute('data-ranking-id'));
        const ids = new Set(existing.map(node => node.dataset.rankingId));
        const hasUnavailable = existing.some(node => node.dataset.rankingAvailable === 'false');
        for (const product of products) {
          if (!product.hasAttribute('data-ranking-id')) continue;
          if (ids.has(product.dataset.rankingId) || (hasUnavailable && product.dataset.rankingAvailable === 'true')) throw new Error('Product order changed between pages');
          ids.add(product.dataset.rankingId);
        }
      }
      for (const product of products) {
        product.dataset.pagination = String(page);
        this.grid.appendChild(product);
      }
      if (this.animations_enabled && products.length) {
        const images = products.map(product => product.querySelector('.product-featured-image-link, .gallery--item figure')).filter(Boolean);
        // An optional animation failure must not make a successful page eligible for duplicate appending.
        try {
          if (images.length) gsap.to(images, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme?.settings?.animation_easing });
        } catch (_) {
          for (const image of images) { image.style.opacity = '1'; image.style.transform = 'none'; }
        }
      }
      const count = parsed.getElementById('ProductCount');
      if (count) for (const item of document.getElementsByClassName('thb-filter-count')) item.innerHTML = count.innerHTML;
      return products.length;
    }
  }
  customElements.define('pagination-theme', Pagination);
}
